<template>
  <div>
    <app-spinner v-if="!is_page_loaded" />
    <template v-else>
      <app-cells position="between">
        <app-cells :indent="false">
          <h1 class="title">Safari (#{{ $route.params.id }})</h1>
          <app-option-picker
            @click="onOptionSelected($event, true)"
            v-model="safari_detail_data.reservation_status"
            :options="safari_detail_data.available_reservation_statuses"
            :disable="safari_detail_data.available_reservation_statuses.length < 1 || (!is_general_manager && !is_safari_supervisor)"
          />
          <div v-if="safari_detail_data.lead" class="manager-title">
            <div class="manager-title__photo">
              <img :src="getUserAvatar(safari_detail_data.lead.manager.crop.crop_url)" alt="Avatar">
            </div>
            <span class="manager-title__name">{{ safari_detail_data.lead.manager.full_name }}</span>
          </div>
          <app-option-picker
            @click="onOptionSelected($event, false)"
            v-model="safari_detail_data.sales_status"
            :options="safari_detail_data.available_sales_statuses"
            :disable="safari_detail_data.available_sales_statuses.length < 1 || (!is_general_manager && !is_sales_manager && !is_sales_supervisor)"
          />
        </app-cells>
        <app-cells :indent="false">
          <a
            v-if="is_super_user"
            :href="`${admin_url}reservations/safari/${$route.params.id}/change/`"
            target="_blank"
            class="btn btn--blue btn--small"
          >
            Django
          </a>
        </app-cells>
      </app-cells>
      <p v-if="itinerary_locked" class="safari-itinerary-shift-notice" role="status">{{ itinerary_lock_message }}</p>
      <SafariCancellationNotice
        v-if="safari_detail_data.sales_status.code === 'cancel' && safari_detail_data.reservation_status.code !== 'cancelled'"
        :summary="safari_detail_data.snackbar_cancellation"
        class="safari-cancellation-summary"
      />
      <section v-if="cancellationResult" class="safari-cancellation-result" role="status">
        <strong>Cancellation completed · {{ cancellationResult.refund_count }} {{ cancellationResult.refund_count === 1 ? 'refund request created' : 'refund requests created' }}</strong>
        <p v-if="cancellationResult.refund_count">The responsible managers will prepare the invoices. Finance will confirm payment.</p>
        <div v-if="cancellationResult.refunds.length" class="safari-cancellation-result__links">
          <router-link v-for="refund in cancellationResult.refunds" :key="refund.id" :to="{ path: '/safari/snack-bar/refunds', query: { refund: String(refund.id) } }">Refund #{{ refund.id }} · {{ refund.client_name }} ↗</router-link>
        </div>
      </section>
<!--      <app-cells>-->
<!--        <router-link-->
<!--          :to="{ name: 'leads-detail-reservations', params: { id: safari_detail_data.lead.id } }"-->
<!--          style="color: #5f5f5f"-->
<!--        >-->
<!--          {{ safari_detail_data.lead.name ? `Main guest: ${safari_detail_data.lead.name}` : `Lead #${safari_detail_data.lead?.id}` }}-->
<!--        </router-link>-->
<!--      </app-cells>-->
      <div
        v-if="safari_detail_data.amendment_request"
        :class="['safari-summary', { 'safari-summary--honeymoon': show_honeymoon_tasks }]"
      >
        <section class="safari-summary__card">
          <div class="safari-summary__header">
            <h2 class="safari-summary__title">Lead &amp; group</h2>
          </div>
          <div class="safari-summary__row">
            <span class="safari-summary__label">Lead</span>
            <div class="safari-summary__value">
              <router-link
                v-if="safari_detail_data.lead"
                :to="{ name: 'leads-detail-reservations', params: { id: safari_detail_data.lead.id } }"
                class="info-cards__link"
              >
                {{ safari_detail_data.lead.name ? safari_detail_data.lead.name : `#${safari_detail_data.lead?.id}` }}
              </router-link>
            </div>
          </div>
          <div v-if="safari_detail_data.sales_status.code === 'book'" class="safari-summary__row">
            <span class="safari-summary__label">Group</span>
            <div class="safari-summary__value">
              <router-link
                v-if="safari_detail_data.safari_group"
                :to="{ name: 'safari-groups-detail', params: { id: safari_detail_data.safari_group.id } }"
                class="info-cards__link"
              >
                {{ safari_detail_data.safari_group.name }}
              </router-link>
              <span v-else class="status-tag" :style="{ 'backgroundColor': '#e85c20' }">Missing</span>
            </div>
          </div>
        </section>
        <section v-if="can_view_group_markers" class="safari-summary__card safari-preferences-card">
          <div class="safari-summary__header">
            <h2 class="safari-summary__title">Preferences</h2>
            <button
              v-if="can_edit_group_markers"
              type="button"
              class="safari-preferences-card__edit"
              aria-label="Edit safari preferences"
              title="Edit safari preferences"
              @click="onShowSafariPreferences"
            >
              <img src="@/assets/img/edit-blue-icon.svg" alt="">
            </button>
          </div>
          <div class="safari-summary__row safari-summary__row--preferences">
            <span class="safari-summary__label">Spoken language</span>
            <strong class="safari-summary__value safari-language-value">
              <i
                v-if="spoken_language_flag_code"
                :class="['fflag', 'ff-sm', `fflag-${spoken_language_flag_code}`]"
                aria-hidden="true"
              />
              <span>{{ spoken_language_name }}</span>
            </strong>
          </div>
          <div class="safari-preferences-card__markers">
            <span
              v-for="marker in active_group_markers"
              :key="marker.code"
              class="safari-preferences-card__marker"
            >
              {{ marker.icon }} {{ marker.label }}
            </span>
            <span v-if="!active_group_markers.length" class="safari-preferences-card__empty">
              No special requests
            </span>
          </div>
        </section>
        <honeymoon-tasks
          v-if="show_honeymoon_tasks"
          :document="honeymoon_document"
          :loading="honeymoon_loading"
          :busy-action="honeymoon_busy_action"
          :can-manage="can_manage_honeymoon"
          @generate="onGenerateHoneymoon"
          @status-change="onHoneymoonStatusChange"
        />
      </div>
      <app-page-container extra>
        <template v-if="safari_detail_data.amendment_request">
          <app-cells>
            <h3 class="title">Amendment actions</h3>
            Reservation status:
            <app-option-picker
              @click="onAmendmentOptionSelected($event, true)"
              v-model="safari_detail_data.amendment_request.reservation_status"
              :options="safari_detail_data.amendment_request.available_reservation_statuses"
              :disable="safari_detail_data.amendment_request.available_reservation_statuses < 1 || (!is_general_manager && !is_safari_supervisor)"
              :amount="safari_detail_data.amendment_request.amount || null"
            />
            Sales status:
            <app-option-picker
              @click="onAmendmentOptionSelected($event, false)"
              v-model="safari_detail_data.amendment_request.sales_status"
              :options="safari_detail_data.amendment_request.available_sales_statuses"
              :disable="safari_detail_data.amendment_request.available_sales_statuses < 1 || (!is_general_manager && !is_reservations_department)"
            />
          </app-cells>
          <app-colored-notes
            v-if="is_reservations_department && safari_detail_data.amendment_request.notes"
            color="#77b017"
            :text="safari_detail_data.amendment_request.notes"
          />
          <app-form-group
            v-if="!is_reservations_department"
            label="Sales manager notes for safari amendment"
            label-for="notes"
          >
            <app-textarea
              @change.native="onPatchReservationAmendmentNotes"
              v-model="safari_detail_data.amendment_request.notes"
              rows="5"
              placeholder="Write a note"
              id="notes"
              :readonly="!is_general_manager && !is_sales_manager && !is_sales_supervisor"
            />
          </app-form-group>
          <app-grid indent>
            <template #item-1>
              <h3 class="title title--mb15" style="color: #008000">The new requirements for the booking</h3>
              <div class="info-cards info-cards--unic-margin">
                <div class="info-cards__card info-cards__card--nominh">
                  <div class="info-cards__row">
                    <div class="info-cards__row-item">
                      <span>Tour Start:</span> <span>{{ dateFormat(safari_detail_data.amendment_request.tour_start) }}</span>
                    </div>
                    <div class="info-cards__row-item">
                      <span>Tour Finish:</span> <span>{{ dateFormat(safari_detail_data.amendment_request.tour_end) }}</span>
                    </div>
                  </div>
                  <div class="info-cards__row">
                    <div class="info-cards__row-item info-cards__row-item--full">
                      <span>Short Itinerary:</span> <span>{{ safari_detail_data.amendment_request.short_itinerary }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="k-v">
                <div v-if="safari_detail_data.amendment_request.type" class="k-v__item">
                  <span class="k-v__k">Group type:</span>&nbsp;<span class="status-tag" :style="{ 'backgroundColor': safari_detail_data.amendment_request.type.color }">{{ safari_detail_data.amendment_request.type.name }}</span>
                </div>
                <app-cells :indent="false" gap="10px">
                  <div class="k-v__item">
                    <span class="k-v__k">Pax:</span>&nbsp;<span class="k-v__v">{{ safari_detail_data.amendment_request.pax }}</span>
                  </div>
                  <div class="k-v__item">
                    <span class="k-v__k">Cars:</span>&nbsp;<span class="k-v__v">{{ safari_detail_data.amendment_request.cars_required }}</span>
                  </div>
                </app-cells>
                <div class="k-v__item">
                  <span class="k-v__k">Start from:</span>&nbsp;<span class="k-v__v">{{ safari_detail_data.amendment_request.start_from.short_name }}</span>
                </div>
                <div class="k-v__item">
                  <span class="k-v__k">Finish at:</span>&nbsp;<span class="k-v__v">{{ safari_detail_data.amendment_request.finish_at.short_name }}</span>
                </div>
              </div>
              <SafariItineraryDayList
                :items="safari_detail_data.amendment_request.itineraries"
                :is-clearable="false"
                :can-add-missing="false"
              />
            </template>
            <template #item-2>
              <h3 class="title title--mb15" style="color: #993300">The details of the original booking</h3>
              <div class="info-cards info-cards--unic-margin">
                <div class="info-cards__card info-cards__card--nominh">
                  <div class="info-cards__row">
                    <div class="info-cards__row-item">
                      <span>Tour Start:</span> <span>{{ dateFormat(safari_detail_data.tour_start) }}</span>
                    </div>
                    <div class="info-cards__row-item">
                      <span>Tour Finish:</span> <span>{{ dateFormat(safari_detail_data.tour_end) }}</span>
                    </div>
                  </div>
                  <div class="info-cards__row">
                    <div class="info-cards__row-item info-cards__row-item--full">
                      <span>Short Itinerary:</span> <span>{{ safari_detail_data.short_itinerary }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="k-v">
                <div class="k-v__item">
                  <span class="k-v__k">Group type:</span>&nbsp;<span class="status-tag" :style="{ 'backgroundColor': safari_detail_data.type.color }">{{ safari_detail_data.type.name }}</span>
                </div>
                <app-cells :indent="false" gap="10px">
                  <div class="k-v__item">
                    <span class="k-v__k">Pax:</span>&nbsp;<span class="k-v__v">{{ safari_detail_data.pax }}</span>
                  </div>
                  <div class="k-v__item">
                    <span class="k-v__k">Cars:</span>&nbsp;<span class="k-v__v">{{ safari_detail_data.cars_required }}</span>
                  </div>
                </app-cells>
                <div class="k-v__item">
                  <span class="k-v__k">Start from:</span>&nbsp;<span class="k-v__v">{{ safari_detail_data.start_from.short_name }}</span>
                </div>
                <div class="k-v__item">
                  <span class="k-v__k">Finish at:</span>&nbsp;<span class="k-v__v">{{ safari_detail_data.finish_at.short_name }}</span>
                </div>
              </div>
              <SafariItineraryDayList
                :items="safari_detail_data.itineraries"
                :is-clearable="false"
                :can-add-missing="false"
              />
            </template>
          </app-grid>
        </template>
        <template v-else>
          <div class="safari-detail-workspace">
            <div class="safari-detail-workspace__column">
              <section class="safari-summary__card">
                <div class="safari-summary__header">
                  <h2 class="safari-summary__title">Lead &amp; group</h2>
                </div>
                <div class="safari-summary__row">
                  <span class="safari-summary__label">Lead</span>
                  <router-link
                    v-if="safari_detail_data.lead"
                    :to="{ name: 'leads-detail-reservations', params: { id: safari_detail_data.lead.id } }"
                    class="info-cards__link safari-summary__value"
                  >
                    {{ safari_detail_data.lead.name || `#${safari_detail_data.lead.id}` }}
                  </router-link>
                </div>
                <div v-if="safari_detail_data.sales_status.code === 'book'" class="safari-summary__row">
                  <span class="safari-summary__label">Group</span>
                  <router-link
                    v-if="safari_detail_data.safari_group"
                    :to="{ name: 'safari-groups-detail', params: { id: safari_detail_data.safari_group.id } }"
                    class="info-cards__link safari-summary__value"
                  >
                    {{ safari_detail_data.safari_group.name }}
                  </router-link>
                  <span v-else class="status-tag" :style="{ backgroundColor: '#e85c20' }">Missing</span>
                </div>
              </section>

              <app-cells v-if="can_request_amendment" :indent="false">
                <app-button @click.native="onShowRequestAmendmentsModal" :disabled="itinerary_locked" theme="warn">Amendment</app-button>
              </app-cells>

              <section class="safari-summary__card safari-tour-card">
                <div class="safari-summary__header">
                  <h2 class="safari-summary__title">Tour overview</h2>
                  <button
                    v-if="is_general_manager"
                    type="button"
                    class="safari-preferences-card__edit"
                    aria-label="Edit safari details"
                    title="Edit safari details"
                    @click="onShowSafariDetailsEditor"
                  >
                    <img src="@/assets/img/edit-blue-icon.svg" alt="">
                  </button>
                </div>
                <div class="safari-tour-card__grid">
                  <div><span>Tour Start</span><strong>{{ dateFormat(safari_detail_data.tour_start) }}</strong></div>
                  <div><span>Tour Finish</span><strong>{{ dateFormat(safari_detail_data.tour_end) }}</strong></div>
                  <div><span>Group type</span><strong>{{ safari_detail_data.type ? safari_detail_data.type.name : '—' }}</strong></div>
                  <div><span>Pax / Cars</span><strong>{{ safari_detail_data.pax || 0 }} / {{ safari_detail_data.cars_required || 0 }}</strong></div>
                  <div><span>Start from</span><strong>{{ safari_detail_data.start_from ? safari_detail_data.start_from.short_name : '—' }}</strong></div>
                  <div><span>Finish at</span><strong>{{ safari_detail_data.finish_at ? safari_detail_data.finish_at.short_name : '—' }}</strong></div>
                </div>
                <div class="safari-tour-card__itinerary">
                  <span>Short itinerary</span>
                  <strong>{{ safari_detail_data.short_itinerary || '—' }}</strong>
                </div>
              </section>

              <section class="safari-summary__card safari-itinerary-card">
                <div class="safari-summary__header">
                  <h2 class="safari-summary__title">Itinerary</h2>
                  <button
                    v-if="is_general_manager"
                    type="button"
                    class="safari-preferences-card__edit"
                    :disabled="itinerary_locked"
                    aria-label="Edit itinerary"
                    title="Edit itinerary"
                    @click="onShowSafariItineraryEditor"
                  >
                    <img src="@/assets/img/edit-blue-icon.svg" alt="">
                  </button>
                </div>
                <SafariItineraryDayList
                  :items="safari_detail_data.itineraries"
                  :is-clearable="false"
                  :can-add-missing="is_general_manager && !itinerary_locked"
                  @add-missing="onSelectMissingItineraryEditorDay"
                />
                <div v-if="!safari_detail_data.itineraries.length" class="safari-itinerary-card__empty">
                  No itinerary days configured
                </div>
              </section>
            </div>

            <div class="safari-detail-workspace__column">
              <app-colored-notes
                v-if="(is_general_manager || is_reservations_department) && safari_detail_data.notes"
                color="#77b017"
                :text="safari_detail_data.notes"
              />
              <app-form-group
                v-if="!is_reservations_department"
                label="Sales manager notes for the safari"
                label-for="notes"
                class="form-group--no-indent safari-manager-notes"
              >
                <app-textarea
                  @change.native="onPatchReservationNotes"
                  v-model="safari_detail_data.notes"
                  rows="5"
                  placeholder="Write a note"
                  id="notes"
                  :readonly="(!is_general_manager && !is_sales_manager && !is_sales_supervisor) && safari_detail_data.reservation_status.code === 'booked'"
                />
              </app-form-group>

              <section v-if="can_view_group_markers" class="safari-summary__card safari-preferences-card">
                <div class="safari-summary__header">
                  <h2 class="safari-summary__title">Preferences</h2>
                  <button
                    v-if="can_edit_group_markers"
                    type="button"
                    class="safari-preferences-card__edit"
                    aria-label="Edit safari preferences"
                    title="Edit safari preferences"
                    @click="onShowSafariPreferences"
                  >
                    <img src="@/assets/img/edit-blue-icon.svg" alt="">
                  </button>
                </div>
                <div class="safari-summary__row safari-summary__row--preferences">
                  <span class="safari-summary__label">Spoken language</span>
                  <strong class="safari-summary__value safari-language-value">
                    <i
                      v-if="spoken_language_flag_code"
                      :class="['fflag', 'ff-sm', `fflag-${spoken_language_flag_code}`]"
                      aria-hidden="true"
                    />
                    <span>{{ spoken_language_name }}</span>
                  </strong>
                </div>
                <div class="safari-preferences-card__markers">
                  <span
                    v-for="marker in active_group_markers"
                    :key="marker.code"
                    class="safari-preferences-card__marker"
                  >
                    {{ marker.icon }} {{ marker.label }}
                  </span>
                  <span v-if="!active_group_markers.length" class="safari-preferences-card__empty">
                    No special requests
                  </span>
                </div>
              </section>

              <honeymoon-tasks
                v-if="show_honeymoon_tasks"
                :document="honeymoon_document"
                :loading="honeymoon_loading"
                :busy-action="honeymoon_busy_action"
                :can-manage="can_manage_honeymoon"
                @generate="onGenerateHoneymoon"
                @status-change="onHoneymoonStatusChange"
              />
            </div>
          </div>
        </template>
      </app-page-container>
      <app-tabs :tabs="tabs_to_show" />
      <transition name="fade" mode="out-in">
        <router-view @get-reservation-data="getSafariData" :reservation-data="safari_detail_data" />
      </transition>
      <modal
        name="safari-preferences"
        :width="500"
        :height="'auto'"
        :scrollable="true"
        :adaptive="true"
        :clickToClose="false"
        class="modal-not-overflow"
        draggable=".popup__title"
        :reset="true"
      >
        <div class="popup">
          <button @click="$modal.hide('safari-preferences')" class="popup__close" type="button"></button>
          <div class="popup__title">Safari preferences</div>
          <div class="popup__body safari-preferences-modal">
            <app-form-field label="Spoken language" required>
              <app-select
                :value="group_markers.spoken_language"
                :options="language_options"
                :reduce="language => language.code"
                label="name"
                placeholder="Select group language"
                :clearable="false"
                :readonly="is_group_markers_saving"
                @input="onGroupMarkerChange('spoken_language', $event)"
              >
                <template #custom-option="language">
                  <span class="safari-language-value">
                    <i
                      v-if="languageFlagCode(language)"
                      :class="['fflag', 'ff-sm', `fflag-${languageFlagCode(language)}`]"
                      aria-hidden="true"
                    />
                    <span>{{ language.name }}</span>
                  </span>
                </template>
              </app-select>
            </app-form-field>
            <div class="safari-preferences-modal__options">
              <app-checkbox
                :value="group_markers.honeymooners"
                label="💍 Honeymoon"
                :readonly="is_group_markers_saving"
                @change="onGroupMarkerChange('honeymooners', $event)"
              />
              <app-checkbox
                :value="group_markers.birdwatchers"
                label="🦜 Birdwatchers"
                :readonly="is_group_markers_saving"
                @change="onGroupMarkerChange('birdwatchers', $event)"
              />
              <app-checkbox
                :value="group_markers.special_assistance"
                label="♿ Accessibility needs"
                :readonly="is_group_markers_saving"
                @change="onGroupMarkerChange('special_assistance', $event)"
              />
            </div>
            <p class="safari-preferences-modal__status" aria-live="polite">
              {{ is_group_markers_saving ? 'Saving changes…' : 'Changes are saved automatically.' }}
            </p>
          </div>
        </div>
      </modal>
      <modal
        name="safari-details-editor"
        :width="850"
        :height="'auto'"
        :scrollable="true"
        :adaptive="true"
        :clickToClose="false"
        class="modal-not-overflow"
        draggable=".popup__title"
        :reset="true"
      >
        <div class="popup">
          <button @click="$modal.hide('safari-details-editor')" class="popup__close" type="button"></button>
          <div class="popup__title">Edit safari details</div>
          <div class="popup__body">
            <div class="safari-details-editor__grid">
              <app-form-field label="Group type">
                <app-select v-model="safari_details_form.type" :options="group_type_options" :clearable="false" />
              </app-form-field>
              <app-form-field label="Pax">
                <app-input v-model.number="safari_details_form.pax" type="number" min="0" />
              </app-form-field>
              <app-form-field label="Cars">
                <app-input v-model.number="safari_details_form.cars_required" type="number" min="0" />
              </app-form-field>
              <app-form-field label="Start from">
                <app-select
                  v-model="safari_details_form.start_from"
                  :options="location_start_options"
                  :clearable="false"
                  placeholder="Start searching"
                  @input="onLocationStartSelected($event, 'safari_details_form')"
                  @search="onLocationStartSearch"
                />
              </app-form-field>
              <app-form-field label="Finish at">
                <app-select
                  v-model="safari_details_form.finish_at"
                  :options="location_finish_options"
                  :clearable="false"
                  placeholder="Start searching"
                  @input="onLocationFinishSelected($event, 'safari_details_form')"
                  @search="onLocationFinishSearch"
                />
              </app-form-field>
            </div>
            <app-cells position="center" :indent="false">
              <app-button ref="save_safari_details" @click.native="onSaveSafariDetails">Save</app-button>
            </app-cells>
          </div>
        </div>
      </modal>
      <modal
        name="safari-itinerary-editor"
        :width="1100"
        :height="'auto'"
        :scrollable="true"
        :adaptive="true"
        :clickToClose="false"
        class="modal-not-overflow"
        draggable=".popup__title"
        :reset="true"
      >
        <div class="popup">
          <button @click="$modal.hide('safari-itinerary-editor')" class="popup__close" type="button"></button>
          <div class="popup__title">Edit itinerary</div>
          <div class="popup__body">
            <div class="safari-itinerary-editor__heading">
              <button type="button" class="safari-itinerary-help-trigger" @click="onShowSafariItineraryHelp(false)">How it works</button>
              <app-button @click.native="onAddItineraryEditorItem">Add at end</app-button>
            </div>
            <p v-if="itinerary_editor_dates_shifted" class="safari-itinerary-shift-notice" role="status">Later dates moved by one day to make room. Review them before saving.</p>
            <div class="safari-itinerary-editor__list">
              <template v-for="entry in editorTimeline(safari_itinerary_form)">
                <SafariItineraryDayEditor
                  v-if="entry.kind === 'day'"
                  :key="entry.item.id || entry.item._editor_key || `itinerary_editor_${entry.index}`"
                  :value="entry.item"
                  :index="entry.dayNumber - 1"
                  :itinerary-types="itinerary_types_options"
                  :national-parks="national_park_options"
                  @input="$set(safari_itinerary_form, entry.index, $event)"
                  @insert-before="onInsertItineraryEditorItem(entry.index)"
                  @remove="safari_itinerary_form.splice(entry.index, 1)"
                />
                <button v-else :key="`editor_missing_${entry.date}`" type="button" class="safari-editor-missing-day" @click="addMissingItineraryDay(safari_itinerary_form, entry.date)">
                  <span>Day {{ entry.dayNumber }} | {{ formatItineraryDate(entry.date) }} · Missing day</span><strong>Add program</strong>
                </button>
              </template>
              <app-no-results v-if="!safari_itinerary_form.length" text="There’s no itinerary yet" small />
              <p v-if="itinerary_editor_error" class="safari-editor-validation" role="alert">{{ itinerary_editor_error }}</p>
            </div>
            <app-cells position="center" :indent="false">
              <app-button ref="save_safari_itinerary" :disabled="!!itinerary_editor_error" @click.native="onSaveSafariItinerary">Save</app-button>
            </app-cells>
          </div>
        </div>
      </modal>
      <modal
        name="deadline"
        :width="reservation_status_data.reservation_status === 'cancelled' ? 540 : 400"
        @before-close="beforeDeadlineClose"
        :height="'auto'"
        :scrollable="true"
        :adaptive="true"
        :clickToClose="false"
        class="modal-not-overflow"
        draggable=".popup__title"
        :reset="true"
      >
        <div class="popup">
          <button @click="onHideDeadlinePopup" class="popup__close" type="button"></button>
          <div class="popup__title">
            {{ reservation_status_data.reservation_status === 'cancelled' ? 'Confirm cancellation' : reservation_status_data.title }}
          </div>
          <div class="popup__body">
            <div v-if="reservation_status_data.reservation_status === 'cancelled'" class="safari-cancellation-confirmation">
              <p v-if="cancellationLoading" role="status">Loading current Snackbar payments…</p>
              <div v-else-if="cancellationError" role="alert"><p>{{ cancellationError }}</p><app-button theme="dark" @click.native="loadCancellationPreview">Retry</app-button></div>
              <template v-else-if="cancellationPreview">
                <SafariCancellationNotice :summary="cancellationPreview" />
                <ul>
                  <li v-if="cancellationPreview.has_group">The Safari Group will be archived and removed from active groups.</li>
                  <li v-else>No active Safari Group is linked to this reservation.</li>
                  <template v-if="Number(cancellationPreview.clients) > 0">
                    <li>The responsible managers will prepare Story invoices for these refunds.</li>
                    <li>Finance will confirm payment. Cancelling the reservation does not return money to the client.</li>
                  </template>
                  <li v-else>No paid Snackbar is recorded. No Snackbar refund requests will be created.</li>
                </ul>
              </template>
            </div>
            <app-form-group v-if="reservation_status_data.reservation_status === 'booked_provisionally'" label="Deadline" required>
              <app-date-picker
                v-model="reservation_status_data.reservation_deadline"
                placeholder="Deadline"
                :error="$v.reservation_status_data.reservation_deadline.$error"
              />
              <template #error>
                <div v-if="$v.reservation_status_data.reservation_deadline.$dirty && !$v.reservation_status_data.reservation_deadline.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Additional message to Sales" label-for="notes">
              <app-textarea
                v-model="reservation_status_data.additional_message"
                rows="5"
                placeholder="Write a message"
                id="notes"
              />
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button @click.native="onCheckDeadlineForm" ref="submit_deadline" :disabled="statusSaving || (reservation_status_data.reservation_status === 'cancelled' && (cancellationLoading || !!cancellationError || !cancellationPreview))">{{ reservation_status_data.reservation_status === 'cancelled' ? 'Confirm cancellation' : 'Save' }}</app-button>
            </app-cells>
          </div>
        </div>
      </modal>
      <modal
        name="safari-request-amendment"
        :width="1200"
        :height="'auto'"
        :scrollable="true"
        :adaptive="true"
        :clickToClose="false"
        class="modal-not-overflow"
        draggable=".popup__title"
        :reset="true"
      >
        <div class="popup">
          <button @click="$modal.hide('safari-request-amendment')" class="popup__close" type="button"></button>
          <div class="popup__title">Request amendment</div>
          <div class="popup__body">
            <form @submit.prevent="onCheckAmendmentSafariForm">
              <div class="safari-amendment-layout">
                <div class="safari-amendment-main">
                  <h3 class="title title--mb15">Main information</h3>
              <app-flex-grid :rows="[[1, '90px', '100px' ], [1, 1], [1, 1]]" align-items="flex-end">
                <template #col-1-1>
                  <app-form-field label="Group Type" required>
                    <app-select
                      v-model="form_safari_amendment.group_type"
                      :options="group_type_options"
                      :clearable="false"
                      placeholder="Group type"
                      :error="$v.form_safari_amendment.group_type.$error"
                    />
                    <template #error>
                      <div v-if="$v.form_safari_amendment.group_type.$dirty && !$v.form_safari_amendment.group_type.required">Required field</div>
                    </template>
                  </app-form-field>
                </template>
                <template #col-1-2>
                  <app-form-field label="Pax" label-for="pax" required>
                    <app-input
                      v-model.number="form_safari_amendment.pax"
                      id="pax"
                      name="pax"
                      type="number"
                    />
                    <template #error>
                      <div v-if="$v.form_safari_amendment.pax.$dirty && !$v.form_safari_amendment.pax.required">Required field</div>
                    </template>
                  </app-form-field>
                </template>
                <template #col-1-3>
                  <app-form-field label="Cars required" label-for="cars_required" required>
                    <app-input
                      v-model.number="form_safari_amendment.cars_required"
                      @change.native="$v.form_safari_amendment.cars_required.$touch()"
                      :error="$v.form_safari_amendment.cars_required.$error"
                      id="cars_required"
                      name="cars_required"
                      type="number"
                    />
                    <template #error>
                      <div v-if="$v.form_safari_amendment.cars_required.$dirty && !$v.form_safari_amendment.cars_required.required">
                        Required field
                      </div>
                    </template>
                  </app-form-field>
                </template>
                <template #col-2-1>
                  <app-form-field label="Start from" required>
                    <app-select
                      @input="onLocationStartSelected($event, 'form_safari_amendment')"
                      v-model="form_safari_amendment.start_from"
                      :options="location_start_options"
                      :clearable="false"
                      @search="onLocationStartSearch"
                      placeholder="Start searching"
                    />
                    <template #error>
                      <div v-if="$v.form_safari_amendment.start_from.$dirty && !$v.form_safari_amendment.start_from.required">Required field</div>
                    </template>
                  </app-form-field>
                </template>
                <template #col-2-2>
                  <app-form-field label="Finish at" required>
                    <app-select
                      @input="onLocationFinishSelected($event, 'form_safari_amendment')"
                      v-model="form_safari_amendment.finish_at"
                      :options="location_finish_options"
                      :clearable="false"
                      @search="onLocationFinishSearch"
                      placeholder="Start searching"
                    />
                    <template #error>
                      <div v-if="$v.form_safari_amendment.finish_at.$dirty && !$v.form_safari_amendment.finish_at.required">Required field</div>
                    </template>
                  </app-form-field>
                </template>
                <template #header-3>
                  <h3 class="title title--mb15">Group details</h3>
                </template>
                <template #col-3-1>
                  <app-form-field label="Spoken language" required>
                    <app-select
                      v-model="form_safari_amendment.spoken_language"
                      :options="language_options"
                      :reduce="language => language.code"
                      label="name"
                      placeholder="Select group language"
                      :error="$v.form_safari_amendment.spoken_language.$error"
                    />
                    <template #error>
                      <div v-if="$v.form_safari_amendment.spoken_language.$dirty && !$v.form_safari_amendment.spoken_language.required">
                        Required field
                      </div>
                    </template>
                  </app-form-field>
                </template>
                <template #col-3-2>
                  <app-cells :indent="false" gap="15px">
                    <app-checkbox v-model="form_safari_amendment.honeymooners" label="💍 Honeymooners"/>
                    <app-checkbox v-model="form_safari_amendment.birdwatchers" label="🦜 Birdwatchers"/>
                    <app-checkbox v-model="form_safari_amendment.special_assistance" label="♿ Special assistance"/>
                  </app-cells>
                </template>
              </app-flex-grid>
              <app-form-group label="Sales manager notes for safari company" label-for="notes">
                <app-textarea
                  v-model="form_safari_amendment.notes"
                  id="notes"
                  placeholder="Write a notes"
                />
              </app-form-group>
                </div>
                <div class="safari-amendment-itinerary">
                  <div class="safari-itinerary-editor__heading">
                    <div class="safari-itinerary-editor__title">
                      <h3 class="title">Itinerary configuration</h3>
                      <button type="button" class="safari-itinerary-help-trigger" @click="onShowSafariItineraryHelp(true)">How it works</button>
                    </div>
                    <app-button @click.native="onAddAmendmentItinerary">Add at end</app-button>
                  </div>
                  <p v-if="amendment_itinerary_loading" role="status">Loading operational details…</p>
                  <p v-else-if="amendment_itinerary_error" role="alert">{{ amendment_itinerary_error }}</p>
                  <p v-if="amendment_dates_shifted" class="safari-itinerary-shift-notice" role="status">Later dates moved by one day to make room. Review them before requesting.</p>
                  <div class="safari-itinerary-editor__list">
                    <template v-for="entry in editorTimeline(itinerary_safari_list)">
                      <SafariItineraryDayEditor
                        v-if="entry.kind === 'day'"
                        :key="entry.item.id || entry.item._editor_key || `amendment_day_${entry.index}`"
                        :value="entry.item"
                        :index="entry.dayNumber - 1"
                        :itinerary-types="itinerary_types_options"
                        :national-parks="national_park_options"
                        :show-operations="true"
                        @input="$set(itinerary_safari_list, entry.index, $event)"
                        @insert-before="onInsertAmendmentItinerary(entry.index)"
                        @remove="onDeleteAmendmentSafariItinerary(entry.index)"
                      />
                      <button v-else :key="`amendment_missing_${entry.date}`" type="button" class="safari-editor-missing-day" @click="addMissingItineraryDay(itinerary_safari_list, entry.date)">
                        <span>Day {{ entry.dayNumber }} | {{ formatItineraryDate(entry.date) }} · Missing day</span><strong>Add program</strong>
                      </button>
                    </template>
                    <app-no-results v-if="!itinerary_safari_list.length" text="There’s no itinerary yet" small />
                    <p v-if="amendment_days_error" class="safari-editor-validation" role="alert">{{ amendment_days_error }}</p>
                  </div>
                </div>
              </div>
              <app-cells position="center" :indent="false">
                <app-button
                  type="submit"
                  ref="submit_safari_amendment"
                  :disabled="$v.form_safari_amendment.$error || !!amendment_days_error || amendment_itinerary_loading || !!amendment_itinerary_error"
                >
                  Request
                </app-button>
              </app-cells>
            </form>
          </div>
        </div>
      </modal>
      <modal
        name="amendment-reservation-accept"
        :width="400"
        :height="'auto'"
        :scrollable="true"
        :adaptive="true"
        :clickToClose="false"
        class="modal-not-overflow"
        draggable=".popup__title"
        :reset="true"
      >
        <div class="popup">
          <button @click="onCloseAcceptAmendmentReservation" class="popup__close" type="button"></button>
          <div class="popup__title popup__title--flex">
            <img src="@/assets/img/warning-round-icon.svg" alt="">
            Change status
          </div>
          <div class="popup__body">
            <div class="popup__subt">
              Are you sure you want to change status to {{ current_amendment_status.name?.toUpperCase() }}?
            </div>
            <p v-if="current_amendment_status.complete && itinerary_locked" class="safari-itinerary-shift-notice" role="status">{{ itinerary_lock_message }}</p>
            <app-form-group
              v-if="current_amendment_status && current_amendment_status.amount_required"
              label="Amount (USD)"
              label-for="cancellation_amount"
              required
            >
              <app-fin-number
                v-model="amendment_amount"
                id="cancellation_amount"
                placeholder="0"
                name="cancellation_amount"
                :error="$v.amendment_amount.$error"
              />
              <template #error>
                <div v-if="$v.amendment_amount.$dirty && !$v.amendment_amount.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button @click.native="onCloseAcceptAmendmentReservation" theme="dark">Cancel</app-button>
              <app-button
                @click.native="onCheckAmendmentReservationStatusForm"
                ref="amendment_reservation_status"
                :disabled="$v.amendment_amount.$error || (current_amendment_status.complete && itinerary_locked)"
              >
                Yes
              </app-button>
            </app-cells>
          </div>
        </div>
      </modal>
      <modal
        name="safari-itinerary-help"
        :width="720"
        :height="'auto'"
        :scrollable="true"
        :adaptive="true"
        :clickToClose="true"
        class="modal-not-overflow"
        :reset="true"
      >
        <div class="popup">
          <button type="button" class="popup__close" aria-label="Close itinerary guide" @click="$modal.hide('safari-itinerary-help')"></button>
          <div class="popup__title">How itinerary editing works</div>
          <div class="popup__body safari-itinerary-help-body">
            <SafariItineraryHelp :show-operations="safari_itinerary_help_show_operations" />
          </div>
        </div>
      </modal>
    </template>
  </div>
</template>

<script>
import AppTabs from '@/components/AppTabs'
import SafariCancellationNotice from '@/components/SafariCancellationNotice.vue'
import {
  getGoogleSearch,
  getLanguages,
  getReservationSafari,
  getReservationsSafariItineraries,
  getReservationSafariHoneymoonDocument,
  getReservationsItineraryTypes,
  getSafariGroupTypes,
  getSafariNationalPark,
  patchReservationSafari,
  patchReservationSafariHoneymoonStatus,
  postReservationSafariHoneymoonGenerate,
  postReservationSafariAmendmentRequest,
  postLocation,
  patchReservationsSafariAmendmentRequest,
  patchReservationsSafari,
} from '@/http'
import AppOptionPicker from '@/components/AppOptionPicker.vue'
import SafariItineraryDayList from '@/components/SafariItineraryDayList.vue'
import { safariItineraryError, safariItineraryTimeline } from '@/utils/safariItineraryDays'
import { addDays, format, parseISO } from 'date-fns'
import debounce from 'lodash/debounce'
import { required, requiredIf } from 'vuelidate/lib/validators'
import AppColoredNotes from '@/components/AppColoredNotes.vue'
import HoneymoonTasks from './components/HoneymoonTasks.vue'
import SafariItineraryDayEditor from './components/SafariItineraryDayEditor.vue'
import SafariItineraryHelp from './components/SafariItineraryHelp.vue'

export default {
  name: 'SafariReservationsDetailLayout',
  components: {
    SafariCancellationNotice,
    AppColoredNotes,
    SafariItineraryDayList,
    AppOptionPicker,
    AppTabs,
    HoneymoonTasks,
    SafariItineraryDayEditor,
    SafariItineraryHelp,
  },
  data() {
    return {
      is_page_loaded: false,
      admin_url: process.env.VUE_APP_ADMIN_URL,
      tabs: [
        { name: 'reservation-safari-detail', label: 'General', roles: [] },
        { name: 'reservation-safari-detail-finance', label: 'Finance', roles: ['general_manager', 'safari_supervisor'] },
        { name: 'reservation-safari-detail-available-cars', label: 'Available Cars', roles: ['general_manager', 'safari_supervisor'] },
        // { name: 'reservation-safari-detail-history', label: 'History', roles: [] },
        { name: 'reservation-safari-detail-reservations', label: 'Reservations', roles: ['general_manager', 'safari_supervisor'] },
      ],
      safari_detail_data: {},
      group_type_options: [],
      location_start_options: [],
      location_finish_options: [],
      itinerary_safari_config: {},
      safari_details_form: {},
      safari_itinerary_form: [],
      itinerary_editor_dates_shifted: false,
      safari_itinerary_help_show_operations: false,
      new_itinerary_row_sequence: 0,
      itinerary_types_options: [],
      area_options: [],
      national_park_options: [],
      language_options: [],
      group_markers: {
        spoken_language: null,
        honeymooners: false,
        birdwatchers: false,
        special_assistance: false,
      },
      group_markers_saving_field: null,
      honeymoon_document: null,
      honeymoon_loading: false,
      honeymoon_busy_action: null,
      language_flag_country: {
        fr: 'FR',
        de: 'DE',
        it: 'IT',
        es: 'ES',
      },
      reservation_status_data: {},
      cancellationPreview: null,
      cancellationLoading: false,
      cancellationError: '',
      cancellationPreviewRequest: 0,
      cancellationResult: null,
      statusSaving: false,
      form_request_amendments: {},
      form_safari_amendment: {},
      itinerary_safari_list: [],
      amendment_dates_shifted: false,
      amendment_itinerary_loading: false,
      amendment_itinerary_error: '',
      tabs_to_show: [],
      current_amendment_status: {},
      amendment_amount: null,
    }
  },
  validations: {
    reservation_status_data: {
      reservation_deadline: {
        required: requiredIf(function() {
          return this.reservation_status_data.reservation_status === 'booked_provisionally'
        })
      },
    },
    form_safari_amendment: {
      cars_required: { required },
      pax: { required },
      start_from: { required },
      finish_at: { required },
      group_type: { required },
      spoken_language: { required },
    },
    amendment_amount: {
      required: requiredIf(function() {
        return this.current_amendment_status && this.current_amendment_status.amount_required
      }),
    },
  },
  created() {
    this.tabs.forEach(item => {
      if (!item.roles.length) {
        this.tabs_to_show.push(item)
        return
      }
      item.roles.forEach(role => {
        if (this.$store.state.userGroups.some(groupRole => groupRole.code === role)) this.tabs_to_show.push(item)
      })
    })
    this.getSafariData()
  },
  computed: {
    itinerary_editor_error() { return safariItineraryError(this.safari_itinerary_form) },
    amendment_days_error() { return safariItineraryError(this.itinerary_safari_list) },
    is_super_user() {
      return this.$store.state.userData.is_superuser
    },
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    is_sales_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_manager')
    },
    is_sales_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_supervisor')
    },
    is_safari_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('safari_supervisor')
    },
    is_safari_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('safari_manager')
    },
    is_reservations_department() {
      return this.$store.state.userGroups.map(item => item.code).includes('reservation_manager')
        || this.$store.state.userGroups.map(item => item.code).includes('reservation_assistant')
        || this.$store.state.userGroups.map(item => item.code).includes('reservation_supervisor')
    },
    can_view_group_markers() {
      const allowedRoles = [
        'general_manager',
        'safari_supervisor',
        'sales_manager',
        'sales_supervisor',
        'safari_manager',
      ]
      return this.$store.state.userGroups.some(item => allowedRoles.includes(item.code))
    },
    can_edit_group_markers() {
      return this.is_general_manager || this.is_safari_supervisor
    },
    show_honeymoon_tasks() {
      return this.can_view_group_markers && this.group_markers.honeymooners
    },
    can_manage_honeymoon() {
      return this.can_edit_group_markers
    },
    itinerary_locked() {
      return Boolean(this.safari_detail_data.itinerary_completed || this.safari_detail_data.itinerary_verified)
    },
    itinerary_lock_message() {
      return 'This itinerary is confirmed or verified. Contact Operations or a supervisor to reopen it before requesting or applying changes.'
    },
    can_request_amendment() {
      const hasRole = this.is_general_manager
        || this.is_sales_manager
        || this.is_sales_supervisor
        || this.is_safari_supervisor
        || this.is_safari_manager
      const hasStatus = ['booked', 'booked_provisionally']
        .includes(this.safari_detail_data.reservation_status.code)
      return hasRole
        && hasStatus
        && !this.safari_detail_data.cancellation_request
        && !this.safari_detail_data.amendment_request
    },
    is_group_markers_saving() {
      return Boolean(this.group_markers_saving_field)
    },
    spoken_language_name() {
      const language = this.language_options.find(item => item.code === this.group_markers.spoken_language)
      return language?.name || 'Not specified'
    },
    spoken_language_flag_code() {
      const language = this.language_options.find(item => item.code === this.group_markers.spoken_language)
      return this.languageFlagCode(language)
    },
    active_group_markers() {
      return [
        { code: 'honeymooners', icon: '💍', label: 'Honeymoon' },
        { code: 'birdwatchers', icon: '🦜', label: 'Birdwatchers' },
        { code: 'special_assistance', icon: '♿', label: 'Accessibility needs' },
      ].filter(item => this.group_markers[item.code])
    },
  },
  methods: {
    formatItineraryDate(date) { return format(parseISO(date), 'dd MMM') },
    editorTimeline(items) {
      return safariItineraryTimeline(items).concat(items
        .map((item, index) => ({ kind: 'day', item, index, dayNumber: index + 1 }))
        .filter(entry => !entry.item.date))
    },
    addMissingItineraryDay(items, date) {
      const index = items.findIndex(item => item.date && item.date > date)
      items.splice(index < 0 ? items.length : index, 0, this.newItineraryDay(date))
    },
    onSelectMissingItineraryEditorDay(date) {
      this.onShowSafariItineraryEditor()
      if (this.itinerary_locked) return
      this.addMissingItineraryDay(this.safari_itinerary_form, date)
    },
    languageFlagCode(language) {
      if (!language) return null
      const countryCode = language.flag_country_code || language.code
      return this.language_flag_country[countryCode] || this.language_flag_country[countryCode?.toLowerCase()]
    },
    onShowSafariPreferences() {
      this.$modal.show('safari-preferences')
    },
    cloneValue(value) {
      return JSON.parse(JSON.stringify(value))
    },
    onShowSafariDetailsEditor() {
      this.safari_details_form = this.cloneValue({
        type: this.safari_detail_data.type,
        pax: this.safari_detail_data.pax,
        cars_required: this.safari_detail_data.cars_required,
        start_from: this.safari_detail_data.start_from,
        finish_at: this.safari_detail_data.finish_at,
      })
      this.$modal.show('safari-details-editor')
    },
    onSaveSafariDetails() {
      const form = this.safari_details_form
      if (!form.type || !form.start_from || !form.finish_at) {
        this.$notify({ type: 'info', title: 'Complete all safari details' })
        return
      }
      this.$refs.save_safari_details.preload = true
      patchReservationSafari(this.$route.params.id, {
        type: form.type.code,
        pax: form.pax,
        cars_required: form.cars_required,
        start_from: form.start_from.id,
        finish_at: form.finish_at.id,
      })
        .finally(() => { this.$refs.save_safari_details.preload = false })
        .then(() => {
          this.$modal.hide('safari-details-editor')
          this.$notify({ type: 'success', title: 'Safari details saved' })
          this.getSafariData()
        })
    },
    onShowSafariItineraryEditor() {
      if (!this.ensureItineraryEditable()) return
      this.safari_itinerary_form = this.cloneValue(this.safari_detail_data.itineraries || [])
      this.itinerary_editor_dates_shifted = false
      this.$modal.show('safari-itinerary-editor')
    },
    onShowSafariItineraryHelp(showOperations) {
      this.safari_itinerary_help_show_operations = showOperations
      this.$modal.show('safari-itinerary-help')
    },
    dateAfter(date, days) {
      return date ? format(addDays(parseISO(date), days), 'yyyy-MM-dd') : null
    },
    newItineraryDay(date = null) {
      this.new_itinerary_row_sequence += 1
      return {
        _editor_key: `new_itinerary_${this.new_itinerary_row_sequence}`,
        date,
        type: this.itinerary_types_options[0] || null,
        national_park: null,
        national_park_area: null,
      }
    },
    insertItineraryDay(days, index) {
      const previous = days[index - 1]
      const next = days[index]
      const date = previous?.date ? this.dateAfter(previous.date, 1) : this.dateAfter(next?.date, -1)
      let shifted = false
      if (previous?.date && next?.date && next.date <= date) {
        for (let position = index; position < days.length; position += 1) {
          if (days[position].date) {
            this.$set(days, position, { ...days[position], date: this.dateAfter(days[position].date, 1) })
            shifted = true
          }
        }
      }
      days.splice(index, 0, this.newItineraryDay(date))
      return shifted
    },
    onInsertItineraryEditorItem(index) {
      if (this.insertItineraryDay(this.safari_itinerary_form, index)) this.itinerary_editor_dates_shifted = true
    },
    onAddItineraryEditorItem() {
      const last = this.safari_itinerary_form[this.safari_itinerary_form.length - 1]
      this.safari_itinerary_form.push(this.newItineraryDay(this.dateAfter(last?.date, 1)))
    },
    onSaveSafariItinerary() {
      if (!this.ensureItineraryEditable()) return
      if (!this.itineraryDaysAreComplete(this.safari_itinerary_form)) return
      this.$refs.save_safari_itinerary.preload = true
      const itineraries = this.safari_itinerary_form.map(item => ({
        id: item.id || null,
        date: item.date,
        type: (item.type || item.itinerary_type)?.code || null,
        national_park: item.national_park?.code || null,
        national_park_area: item.national_park_area?.id || null,
      }))
      patchReservationSafari(this.$route.params.id, { itineraries })
        .finally(() => { this.$refs.save_safari_itinerary.preload = false })
        .then(() => {
          this.$modal.hide('safari-itinerary-editor')
          this.$notify({ type: 'success', title: 'Itinerary saved' })
          this.getSafariData()
        })
    },
    onGroupMarkerChange(field, value) {
      if (!this.can_edit_group_markers || this.is_group_markers_saving) return
      if (this.group_markers[field] === value) return

      const previousValue = this.group_markers[field]
      this.$set(this.group_markers, field, value)
      this.group_markers_saving_field = field

      patchReservationSafari(this.$route.params.id, { [field]: value })
        .then(() => {
          this.$set(this.safari_detail_data, field, value)
          if (field !== 'honeymooners') return
          if (value) this.getHoneymoonDocument()
          else this.honeymoon_document = null
        })
        .catch(() => {
          this.$set(this.group_markers, field, previousValue)
          this.$notify({ type: 'error', title: 'Safari preferences could not be saved' })
        })
        .finally(() => {
          this.group_markers_saving_field = null
        })
    },
    getHoneymoonDocument() {
      if (!this.show_honeymoon_tasks) {
        this.honeymoon_document = null
        return Promise.resolve()
      }
      this.honeymoon_loading = true
      return getReservationSafariHoneymoonDocument(this.$route.params.id)
        .then(response => {
          this.honeymoon_document = response.data
        })
        .catch(() => {
          this.$notify({ type: 'error', title: 'Honeymoon checklist could not be loaded' })
        })
        .finally(() => {
          this.honeymoon_loading = false
        })
    },
    onGenerateHoneymoon(names) {
      if (!this.can_manage_honeymoon || this.honeymoon_busy_action) return
      this.honeymoon_busy_action = 'generate'
      postReservationSafariHoneymoonGenerate(this.$route.params.id, names)
        .then(response => {
          this.honeymoon_document = response.data
          this.$notify({
            type: 'success',
            title: 'Honeymoon PDF is ready',
          })
        })
        .finally(() => {
          this.honeymoon_busy_action = null
        })
    },
    onHoneymoonStatusChange(status, value) {
      if (!this.can_manage_honeymoon || this.honeymoon_busy_action) return
      this.honeymoon_busy_action = status
      patchReservationSafariHoneymoonStatus(
        this.$route.params.id,
        status,
        value
      )
        .then(response => {
          this.honeymoon_document = response.data
        })
        .catch(() => this.getHoneymoonDocument())
        .finally(() => {
          this.honeymoon_busy_action = null
        })
    },
    onPatchReservationNotes() {
      patchReservationsSafari(this.$route.params.id, { notes: this.safari_detail_data.notes })
    },
    onPatchReservationAmendmentNotes() {
      patchReservationsSafariAmendmentRequest(
        this.safari_detail_data.amendment_request.id,
        { notes: this.safari_detail_data.amendment_request.notes }
      )
        .then(() => { this.getReservationData() })
    },
    onCheckAmendmentReservationStatusForm() {
      this.$v.amendment_amount.$touch()
      if (this.$v.amendment_amount.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.changeAmendmentReservationStatus()
      }
    },
    changeAmendmentReservationStatus() {
      if (this.current_amendment_status.complete && !this.ensureItineraryEditable()) return
      this.$refs.amendment_reservation_status.preload = true
      patchReservationsSafariAmendmentRequest(
        this.safari_detail_data.amendment_request.id,
        { reservation_status: this.current_amendment_status.code, amount: this.amendment_amount }
      )
        .finally(() => { this.$refs.amendment_reservation_status.preload = false })
        .then(() => {
          this.getSafariData()
          this.$modal.hide('amendment-reservation-accept')
        })
    },
    onCloseAcceptAmendmentReservation() {
      this.is_page_loaded = false
      this.$modal.hide('amendment-reservation-accept')
      this.getSafariData()
    },
    onAmendmentOptionSelected(event, is_reservation) {
      this.current_amendment_status = event
      if (is_reservation) this.$modal.show('amendment-reservation-accept')
      else this.changeAmendmentSalesStatus()
    },
    changeAmendmentSalesStatus() {
      patchReservationsSafariAmendmentRequest(
        this.safari_detail_data.amendment_request.id,
        { sales_status: this.current_amendment_status.code }
      )
        .then(() => { this.getSafariData() })
    },
    onCheckAmendmentSafariForm() {
      this.$v.form_safari_amendment.$touch()
      if (this.$v.form_safari_amendment.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else if (this.itineraryDaysAreComplete(this.itinerary_safari_list)) {
        this.sendAmendmentSafariForm()
      }
    },
    ensureItineraryEditable() {
      if (!this.itinerary_locked) return true
      this.$notify({ type: 'info', title: this.itinerary_lock_message })
      return false
    },
    itineraryDaysAreComplete(days) {
      const error = safariItineraryError(days)
      if (!error && days.every(day => day.type || day.itinerary_type)) return true
      this.$notify({ type: 'info', title: error || 'Add a type for every itinerary day' })
      return false
    },
    sendAmendmentSafariForm() {
      if (!this.ensureItineraryEditable()) return
      this.$refs.submit_safari_amendment.preload = true
      postReservationSafariAmendmentRequest(this.normalizeAmendmentSafariForm())
        .finally(() => { this.$refs.submit_safari_amendment.preload = false })
        .then(() => {
          this.getSafariData()
          this.$modal.hide('safari-request-amendment')
          this.$notify({
            type: 'success',
            title: 'Request has been sent successfully',
          })
        })
    },
    normalizeAmendmentSafariForm() {
      const normalizedForm = { ...this.form_safari_amendment }

      normalizedForm.reservation = +this.$route.params.id
      normalizedForm.sales_status = 'change'
      if (normalizedForm.group_type) normalizedForm.type = normalizedForm.group_type.code
      if (normalizedForm.start_from) normalizedForm.start_from = normalizedForm.start_from.id
      if (normalizedForm.finish_at) normalizedForm.finish_at = normalizedForm.finish_at.id
      if (this.itinerary_safari_list && this.itinerary_safari_list.length > 0) {
        normalizedForm.itineraries = this.itinerary_safari_list.map(item => ({
          original_itinerary: item.id || null,
          date: item.date,
          type: (item.type || item.itinerary_type)?.code || null,
          national_park: item.national_park?.code || null,
          national_park_area: item.national_park_area?.id || null,
        }))
      }
      delete normalizedForm.group_type

      return normalizedForm
    },
    onDeleteAmendmentSafariItinerary(index) {
      this.itinerary_safari_list.splice(index, 1)
    },
    async onShowRequestAmendmentsModal() {
      if (!this.ensureItineraryEditable()) return
      this.form_safari_amendment = {
        group_type: this.safari_detail_data.type,
        pax: this.safari_detail_data.pax,
        cars_required: this.safari_detail_data.cars_required,
        start_from: this.safari_detail_data.start_from,
        finish_at: this.safari_detail_data.finish_at,
        spoken_language: this.safari_detail_data.spoken_language?.code || null,
        honeymooners: Boolean(this.safari_detail_data.honeymooners),
        birdwatchers: Boolean(this.safari_detail_data.birdwatchers),
        special_assistance: Boolean(this.safari_detail_data.special_assistance),
      }
      this.itinerary_safari_list = this.cloneValue(this.safari_detail_data.itineraries || [])
      this.amendment_dates_shifted = false
      this.amendment_itinerary_error = ''
      this.amendment_itinerary_loading = true
      this.$modal.show('safari-request-amendment')
      try {
        const { data } = await getReservationsSafariItineraries(this.$route.params.id)
        const operations = new Map(data.map(day => [day.id, day]))
        this.itinerary_safari_list = this.itinerary_safari_list.map(day => ({
          ...day,
          notes: operations.has(day.id) ? operations.get(day.id).notes : day.notes,
          crater_transit: operations.has(day.id) ? operations.get(day.id).crater_transit : day.crater_transit,
          timings: operations.get(day.id)?.timings || [],
        }))
      } catch (error) {
        this.amendment_itinerary_error = 'Could not load the current operational details. Please reopen the request.'
      } finally {
        this.amendment_itinerary_loading = false
      }
    },
    onCheckDeadlineForm() {
      if (this.statusSaving || (this.reservation_status_data.reservation_status === 'cancelled' && (this.cancellationLoading || this.cancellationError || !this.cancellationPreview))) return
      this.$v.reservation_status_data.$touch()
      if (this.$v.reservation_status_data.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.patchDeadline()
      }
    },
    async loadCancellationPreview() {
      const current = ++this.cancellationPreviewRequest
      this.cancellationLoading = true
      this.cancellationError = ''
      this.cancellationPreview = null
      try {
        const { data } = await getReservationSafari(this.$route.params.id)
        if (current !== this.cancellationPreviewRequest) return
        if (!data.snackbar_cancellation || data.snackbar_cancellation.clients === undefined) throw new Error('Cancellation preview is unavailable')
        this.cancellationPreview = { ...data.snackbar_cancellation, has_group: Boolean(data.safari_group) }
      } catch (error) {
        if (current === this.cancellationPreviewRequest) this.cancellationError = 'Unable to load the current cancellation details. Please retry.'
      } finally { if (current === this.cancellationPreviewRequest) this.cancellationLoading = false }
    },
    beforeDeadlineClose(event) {
      if (this.statusSaving) { event.cancel(); return }
      this.cancellationPreviewRequest += 1
    },
    patchDeadline() {
      this.statusSaving = true
      this.$refs.submit_deadline.preload = true
      const cancelling = this.reservation_status_data.reservation_status === 'cancelled'
      patchReservationSafari(
        this.$route.params.id,
        {
          reservation_status: this.reservation_status_data.reservation_status,
          reservation_deadline: this.reservation_status_data.reservation_deadline || null,
          additional_message: this.reservation_status_data.additional_message,
        }
      )
        .finally(() => { this.statusSaving = false; this.$refs.submit_deadline.preload = false })
        .then(({ data }) => {
          this.cancellationResult = cancelling ? data.cancellation_result || null : null
          this.$notify({ type: 'success', title: cancelling ? 'Cancellation completed' : 'Status changed' })
          this.$modal.hide('deadline')
          this.getSafariData()
        })
        .catch(error => {
          if (cancelling) this.cancellationError = error.response?.data?.details || 'Cancellation could not be completed. Refresh the preview and try again.'
        })
    },
    onHideDeadlinePopup() {
      if (this.statusSaving) return
      this.is_page_loaded = false
      this.$modal.hide('deadline')
      this.getSafariData()
    },
    onPatchReservationStatus() {
      patchReservationSafari(this.$route.params.id, this.reservation_status_data)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Status changed',
          })
          this.getSafariData()
        })
    },
    onOptionSelected(e, deadline) {
      const data_reservation = {
        reservation_status: e.code,
        title: e.name,
        additional_message: ''
      }
      const data_sales = { sales_status: e.code }
      if (e.code === 'booked_provisionally') data_reservation.reservation_deadline = null
      this.onShowAcceptReservationStatusPatch(deadline ? data_reservation : data_sales, deadline)
    },
    onShowAcceptReservationStatusPatch(data, deadline) {
      this.reservation_status_data = data
      this.reservation_status_data.additional_message = this.safari_detail_data.additional_message || null
      if (!deadline) this.onPatchReservationStatus()
      else {
        this.$v.reservation_status_data.$reset()
        this.$modal.show('deadline')
        if (data.reservation_status === 'cancelled') this.loadCancellationPreview()
      }
    },
    onAddItinerary() {
      this.safari_detail_data.itineraries.push({ ...this.itinerary_safari_config, type: this.itinerary_safari_config.itinerary_type })
    },
    onNationalParkSelected() {
      delete this.itinerary_safari_config.national_park_area
      this.area_options = this.itinerary_safari_config.national_park.area
    },
    onItineraryTypeSelected() {
      if (this.itinerary_safari_config.itinerary_type.code === 'rest_day') {
        this.itinerary_safari_config.national_park = null
        this.itinerary_safari_config.national_park_area = null
      }
    },
    onAddAmendmentItinerary() {
      const last = this.itinerary_safari_list[this.itinerary_safari_list.length - 1]
      this.itinerary_safari_list.push(this.newItineraryDay(this.dateAfter(last?.date, 1)))
    },
    onInsertAmendmentItinerary(index) {
      if (this.insertItineraryDay(this.itinerary_safari_list, index)) this.amendment_dates_shifted = true
    },
    onConfiguratorSave() {
      this.$refs.configurator_save.preload = true
      patchReservationSafari(this.$route.params.id, this.normalizeConfigurator())
        .finally(() => { this.$refs.configurator_save.preload = false })
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Data saved successfully',
          })
        })
    },
    normalizeConfigurator() {
      const form = {}

      form.type = this.safari_detail_data.type?.code || null
      form.pax = this.safari_detail_data.pax
      form.cars_required = this.safari_detail_data.cars_required
      form.start_from = this.safari_detail_data.start_from.id
      form.finish_at = this.safari_detail_data.finish_at.id
      if (this.safari_detail_data.itineraries && this.safari_detail_data.itineraries.length > 0) {
        form.itineraries = this.safari_detail_data.itineraries.map(item => ({
          id: item.id || null,
          date: item.date,
          type: item.type?.code || null,
          national_park: item.national_park?.code || null,
          national_park_area: item.national_park_area?.id || null,
        }))
      }

      return form
    },
    onDeleteSafariItinerary(index) {
      this.safari_detail_data.itineraries.splice(index, 1)
    },
    getSafariData() {
      Promise.all([
        getReservationSafari(this.$route.params.id),
        getSafariGroupTypes({ reservations: true }),
        getReservationsItineraryTypes(),
        getSafariNationalPark({ reservations: true }),
        getLanguages()
      ])
        .then(res => {
          this.safari_detail_data = res[0].data
          this.group_type_options = res[1].data
          this.itinerary_types_options = res[2].data
          this.itinerary_safari_config.itinerary_type = res[2].data[0]
          this.itinerary_safari_config.date = res[0].data.tour_start
          this.national_park_options = res[3].data
          this.language_options = res[4].data
          this.group_markers = {
            spoken_language: this.safari_detail_data.spoken_language?.code || null,
            honeymooners: Boolean(this.safari_detail_data.honeymooners),
            birdwatchers: Boolean(this.safari_detail_data.birdwatchers),
            special_assistance: Boolean(this.safari_detail_data.special_assistance),
          }
          if (this.show_honeymoon_tasks) this.getHoneymoonDocument()
          else this.honeymoon_document = null
          if (this.safari_detail_data.reservation_status.code === 'cancelled') this.tabs.splice(2, 1)
          document.title = `Safari (#${this.$route.params.id}) | Altezza Board`
          this.is_page_loaded = true
        })
    },
    onLocationStartSearch(search, loading) {
      loading(true)
      this.performLocationSearch(search, loading, 'location_start_options', this)
    },
    onLocationFinishSearch(search, loading) {
      loading(true)
      this.performLocationSearch(search, loading, 'location_finish_options', this)
    },
    performLocationSearch: debounce((search, loading, fieldKey, vm) => {
      if (!search) {
        vm[fieldKey] = []
        loading(false)
      } else {
        getGoogleSearch(search).then(response => {
          vm[fieldKey] = response.data
          loading(false)
        })
      }
    }, 350),
    onLocationStartSelected({ name, place_id }, form) {
      postLocation({ name, place_id }).then(res => {
        this[form].start_from = res.data
      })
    },
    onLocationFinishSelected({ name, place_id }, form) {
      postLocation({ name, place_id }).then(res => {
        this[form].finish_at = res.data
      })
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
    dateFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy') : '-'
    },
  }
}
</script>

<style scoped>
.safari-summary {
  display: grid;
  grid-template-columns: minmax(280px, 400px) minmax(320px, 520px);
  gap: 20px;
  align-items: stretch;
  margin: 24px 0 30px;
}
.safari-summary--honeymoon {
  grid-template-columns: minmax(280px, 400px) minmax(320px, 460px) minmax(360px, 520px);
}
.safari-summary__card {
  box-sizing: border-box;
  padding: 18px 20px;
  border: 1px solid #e2e2e2;
  border-radius: 10px;
  background: #fff;
}
.safari-summary__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
}
.safari-summary__title { margin: 0; color: #202020; font-size: 16px; line-height: 25px; }
.safari-summary__row {
  display: grid;
  grid-template-columns: 70px minmax(0, 1fr);
  gap: 14px;
  align-items: baseline;
}
.safari-summary__row + .safari-summary__row { margin-top: 14px; }
.safari-summary__row--preferences { grid-template-columns: 125px minmax(0, 1fr); }
.safari-summary__label { color: #606060; font-size: 14px; }
.safari-summary__value { min-width: 0; color: #202020; font-size: 15px; line-height: 20px; }
.safari-summary__value.info-cards__link,
.safari-summary__value .info-cards__link {
  display: inline-block;
  width: fit-content;
  justify-self: start;
}
.safari-detail-workspace {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(380px, .95fr);
  gap: 24px;
  align-items: start;
  margin-bottom: 30px;
}
.safari-detail-workspace__column {
  display: grid;
  min-width: 0;
  gap: 20px;
  align-content: start;
}
.safari-tour-card__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 22px;
}
.safari-tour-card__grid div,
.safari-tour-card__itinerary {
  display: grid;
  gap: 4px;
}
.safari-tour-card__grid span,
.safari-tour-card__itinerary span { color: #606060; font-size: 12px; }
.safari-tour-card__grid strong,
.safari-tour-card__itinerary strong { color: #202020; font-size: 14px; line-height: 20px; }
.safari-tour-card__itinerary { margin-top: 16px; }
.safari-itinerary-card__empty { padding: 18px 0; color: #8a8a8a; font-size: 13px; }
.safari-details-editor__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
  margin-bottom: 24px;
}
.safari-itinerary-editor__heading { display: flex; align-items: center; justify-content: space-between; gap: 18px; margin-bottom: 18px; }
.safari-itinerary-editor__title { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; min-width: 0; }
.safari-itinerary-help-trigger { padding: 5px 9px; border: 1px solid #dce2e7; border-radius: 6px; background: #fff; color: #438007; cursor: pointer; font-size: 12px; white-space: nowrap; }
.safari-itinerary-help-trigger:hover, .safari-itinerary-help-trigger:focus-visible { background: #f6fbef; }
.safari-itinerary-help-trigger:focus-visible { outline: 2px solid #438007; outline-offset: 2px; }
.safari-itinerary-help-body { padding-bottom: 28px; }
.safari-itinerary-shift-notice { margin: 0 0 14px; padding: 10px 12px; border-radius: 6px; background: #fff5dd; color: #755424; font-size: 13px; }
.safari-amendment-layout { display: grid; grid-template-columns: minmax(0, .9fr) minmax(0, 1.1fr); gap: 28px; margin-bottom: 24px; }
.safari-amendment-main, .safari-amendment-itinerary { min-width: 0; }
.safari-amendment-itinerary { padding-left: 26px; border-left: 1px solid #e5e5e5; }
.safari-itinerary-editor__list { margin-bottom: 22px; }
.safari-editor-missing-day { display: flex; align-items: center; justify-content: space-between; gap: 12px; width: 100%; margin: 12px 0; padding: 12px 14px; border: 1px dashed #d69b45; border-radius: 9px; background: #fffaf1; color: #6d4a19; text-align: left; cursor: pointer; }
.safari-editor-missing-day strong { color: #356c9b; white-space: nowrap; }
.safari-editor-missing-day:hover, .safari-editor-missing-day:focus-visible { background: #fff2dc; }
.safari-editor-validation { margin: 10px 0 0; color: #a14b22; font-size: 13px; }
.safari-language-value { display: inline-flex; gap: 7px; align-items: center; }
.safari-preferences-card__edit {
  display: flex;
  width: 25px;
  height: 25px;
  padding: 0;
  border: 0;
  border-radius: 9px;
  background: transparent;
  cursor: pointer;
}
.safari-preferences-card__edit:focus-visible { outline: 2px solid #35a4ea; outline-offset: 2px; }
.safari-preferences-card__edit img { display: block; width: 25px; height: 25px; }
.safari-preferences-card__markers { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 14px; }
.safari-preferences-card__marker {
  padding: 5px 9px;
  border-radius: 14px;
  background: #f2f6ec;
  color: #3f4a35;
  font-size: 13px;
  line-height: 18px;
}
.safari-preferences-card__empty { color: #8a8a8a; font-size: 13px; line-height: 18px; }
.safari-cancellation-summary { margin-bottom: 20px; }
.safari-cancellation-confirmation { margin-bottom: 20px; }
.safari-cancellation-confirmation ul { margin: 14px 0 0; padding-left: 20px; line-height: 1.6; }
.safari-cancellation-confirmation li + li { margin-top: 8px; }
.safari-cancellation-result { box-sizing: border-box; max-width: 1080px; margin-bottom: 20px; padding: 16px; border: 1px solid #b9d89b; border-left: 4px solid #77b017; border-radius: 8px; background: #f1f8e9; }
.safari-cancellation-result strong { font-size: 16px; }
.safari-cancellation-result p { margin: 8px 0 0; line-height: 1.5; }
.safari-cancellation-result__links { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 12px; }
.safari-cancellation-result__links a { padding: 6px 10px; border: 1px solid #b9d89b; border-radius: 6px; background: #fff; color: #326513; }
.safari-manager-notes { margin-bottom: 24px; }
.safari-preferences-modal__options {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 22px;
}
.safari-preferences-modal__status { margin: 20px 0 0; color: #8a8a8a; font-size: 12px; }
@media (max-width: 760px) {
  .safari-summary { grid-template-columns: 1fr; }
  .safari-detail-workspace { grid-template-columns: 1fr; }
  .safari-tour-card__grid,
  .safari-details-editor__grid,
  .safari-amendment-layout { grid-template-columns: 1fr; }
  .safari-amendment-itinerary { padding-left: 0; border-left: 0; }
  .safari-itinerary-editor__heading { align-items: flex-start; }
}
@media (min-width: 761px) and (max-width: 1380px) {
  .safari-summary--honeymoon { grid-template-columns: repeat(2, minmax(280px, 1fr)); }
  .safari-detail-workspace { grid-template-columns: minmax(0, 1fr) minmax(340px, .85fr); }
  .safari-details-editor__grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
</style>
